import React, { useState, useEffect } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity, Linking,
} from 'react-native';
import Icon from 'react-native-vector-icons/Ionicons';
import { THEME } from '../components';
import { useLanguage } from '../context/LanguageContext';
import { getFaqs, getAppConfig } from '../services/api';
import type { StackScreenProps } from '@react-navigation/stack';
import type { RootStackParamList, FaqApiItem, AppConfig } from '../types';

type Props = StackScreenProps<RootStackParamList, 'Help'>;

const SUPPORT_EMAIL = 'support@jeevansanyog.com';

interface FaqItem {
  q: string;
  a: string;
}

const FAQ_EN: FaqItem[] = [
  {
    q: 'How long does profile approval take?',
    a: 'Profiles are reviewed by our team within 24–48 hours. You will be notified once your profile is approved or if any changes are required.',
  },
  {
    q: 'Why was my profile rejected?',
    a: 'Profiles may be rejected if the information is incomplete, photos are inappropriate, or the details do not meet our guidelines. Edit your profile addressing the rejection reason and resubmit.',
  },
  {
    q: 'How do I upload or change my photo?',
    a: "Go to My Profile → tap the Photo button. You can upload up to the allowed limit. Photos are reviewed before being made public. Only the main photo appears on your profile card.",
  },
  {
    q: 'What is the difference between deactivating and deleting my profile?',
    a: 'Deactivating hides your profile from other users but keeps all your data safe. You can reactivate anytime. Deleting schedules permanent removal after a 30-day grace period — this cannot be undone after the grace period ends.',
  },
  {
    q: 'How do I cancel a scheduled deletion?',
    a: 'Go to My Profile. If your profile is in "Deletion Pending" state, you will see a "Cancel Deletion" button. Tap it to restore your profile to Active.',
  },
  {
    q: 'What membership plans are available?',
    a: 'We offer Free Trial, Basic, Standard, and Premium plans. Each plan gives different contact view limits and features. Visit the Membership tab to see current plans and pricing.',
  },
  {
    q: 'How do I view contact details?',
    a: 'Open a profile and tap "View Contact". You need an active membership with remaining contact views. Contact views are counted per unique profile.',
  },
  {
    q: 'My payment was deducted but membership was not activated. What do I do?',
    a: 'This is rare and usually resolves within a few minutes. If the issue persists after 30 minutes, contact us via WhatsApp or email with your payment details and we will resolve it promptly.',
  },
  {
    q: 'How do I send or respond to an interest?',
    a: 'Open a profile and tap "Send Interest". To respond to interests received, go to the Interests tab. You can Accept or Reject each interest.',
  },
  {
    q: 'Is my data safe?',
    a: 'Yes. Contact details are only visible to users with an active membership who explicitly tap "View Contact". Photos are stored securely on Azure. We never share your data with third parties.',
  },
];

const FAQ_HI: FaqItem[] = [
  {
    q: 'प्रोफ़ाइल अनुमोदन में कितना समय लगता है?',
    a: 'हमारी टीम 24–48 घंटों के भीतर प्रोफ़ाइल की समीक्षा करती है। अनुमोदन या बदलाव की आवश्यकता होने पर आपको सूचित किया जाएगा।',
  },
  {
    q: 'मेरी प्रोफ़ाइल अस्वीकार क्यों हुई?',
    a: 'प्रोफ़ाइल अधूरी जानकारी, अनुचित फ़ोटो, या हमारे दिशानिर्देशों का पालन न करने पर अस्वीकार हो सकती है। कारण सुधारकर दोबारा जमा करें।',
  },
  {
    q: 'फ़ोटो कैसे अपलोड या बदलें?',
    a: 'My Profile → फ़ोटो बटन दबाएं। फ़ोटो सार्वजनिक होने से पहले समीक्षा की जाती है। मुख्य फ़ोटो ही प्रोफ़ाइल कार्ड पर दिखती है।',
  },
  {
    q: 'प्रोफ़ाइल निष्क्रिय और हटाने में क्या अंतर है?',
    a: 'निष्क्रिय करने पर प्रोफ़ाइल छुप जाती है लेकिन डेटा सुरक्षित रहता है, जिसे कभी भी पुनः सक्रिय किया जा सकता है। हटाने पर 30 दिन की अवधि के बाद डेटा स्थायी रूप से मिट जाता है।',
  },
  {
    q: 'निर्धारित विलोपन कैसे रद्द करें?',
    a: 'My Profile पर जाएं। यदि प्रोफ़ाइल "Deletion Pending" अवस्था में है तो "Cancel Deletion" बटन दिखेगा। उसे दबाएं।',
  },
  {
    q: 'कौन-कौन से सदस्यता प्लान उपलब्ध हैं?',
    a: 'Free Trial, Basic, Standard, और Premium प्लान उपलब्ध हैं। Membership टैब पर जाकर वर्तमान प्लान और मूल्य देखें।',
  },
  {
    q: 'संपर्क विवरण कैसे देखें?',
    a: 'किसी प्रोफ़ाइल को खोलें और "View Contact" दबाएं। इसके लिए सक्रिय सदस्यता और शेष व्यू आवश्यक हैं।',
  },
  {
    q: 'भुगतान कट गया लेकिन सदस्यता सक्रिय नहीं हुई?',
    a: 'यह आमतौर पर कुछ मिनटों में ठीक हो जाता है। 30 मिनट बाद भी समस्या रहे तो WhatsApp या ईमेल से भुगतान विवरण सहित संपर्क करें।',
  },
  {
    q: 'इंटरेस्ट कैसे भेजें या स्वीकार करें?',
    a: 'किसी प्रोफ़ाइल पर "Send Interest" दबाएं। प्राप्त इंटरेस्ट देखने के लिए Interests टैब पर जाएं।',
  },
  {
    q: 'क्या मेरा डेटा सुरक्षित है?',
    a: 'हाँ। संपर्क विवरण केवल सक्रिय सदस्यता वाले उपयोगकर्ताओं को दिखता है। फ़ोटो Azure पर सुरक्षित संग्रहीत हैं। आपका डेटा तृतीय पक्षों से साझा नहीं किया जाता।',
  },
];

const FaqRow: React.FC<{ item: FaqItem }> = ({ item }) => {
  const [open, setOpen] = useState(false);
  return (
    <TouchableOpacity
      style={styles.faqItem}
      onPress={() => setOpen(o => !o)}
      activeOpacity={0.8}>
      <View style={styles.faqHeader}>
        <Text style={styles.faqQ}>{item.q}</Text>
        <Icon
          name={open ? 'chevron-up' : 'chevron-down'}
          size={18}
          color={THEME.textLight}
        />
      </View>
      {open && <Text style={styles.faqA}>{item.a}</Text>}
    </TouchableOpacity>
  );
};

const HelpScreen: React.FC<Props> = ({ route }) => {
  const { lang, t } = useLanguage();
  const profileCode = route.params?.profileCode;
  const [faqs, setFaqs] = useState<FaqItem[]>(lang === 'hi' ? FAQ_HI : FAQ_EN);
  const [config, setConfig] = useState<AppConfig>({
    supportWhatsApp: '',
    supportEmail: SUPPORT_EMAIL,
  });

  useEffect(() => {
    Promise.allSettled([getFaqs(), getAppConfig()]).then(([faqResult, configResult]) => {
      if (faqResult.status === 'fulfilled') {
        const items: FaqApiItem[] = faqResult.value.data ?? [];
        if (items.length > 0) {
          setFaqs(items.map(f => ({
            q: lang === 'hi' ? (f.q.hi || f.q.en) : f.q.en,
            a: lang === 'hi' ? (f.a.hi || f.a.en) : f.a.en,
          })));
        } else {
          setFaqs(lang === 'hi' ? FAQ_HI : FAQ_EN);
        }
      } else {
        setFaqs(lang === 'hi' ? FAQ_HI : FAQ_EN);
      }
      if (configResult.status === 'fulfilled') {
        const c: AppConfig = configResult.value.data ?? {};
        setConfig({
          supportWhatsApp: c.supportWhatsApp || '',
          supportEmail: c.supportEmail || SUPPORT_EMAIL,
          supportHoursEn: c.supportHoursEn,
          supportHoursHi: c.supportHoursHi,
        });
      }
    });
  }, [lang]);

  const supportHours = lang === 'hi'
    ? (config.supportHoursHi || config.supportHoursEn || t('msg.supportHours', 'Support hours: Monday – Saturday, 9 AM – 7 PM IST'))
    : (config.supportHoursEn || t('msg.supportHours', 'Support hours: Monday – Saturday, 9 AM – 7 PM IST'));

  const openWhatsApp = () => {
    const message = profileCode
      ? `Hi, I need help with my JeevanSanyog profile. Profile code: ${profileCode}`
      : 'Hi, I need help with JeevanSanyog.';
    const url = `https://wa.me/${config.supportWhatsApp}?text=${encodeURIComponent(message)}`;
    Linking.openURL(url).catch(() => {});
  };

  const openEmail = () => {
    const subject = profileCode
      ? `JeevanSanyog Support - Profile ${profileCode}`
      : 'JeevanSanyog Support';
    const url = `mailto:${config.supportEmail}?subject=${encodeURIComponent(subject)}`;
    Linking.openURL(url).catch(() => {});
  };

  return (
    <ScrollView style={styles.container} contentContainerStyle={{ paddingBottom: 40 }}>
      {/* Header banner */}
      <View style={styles.banner}>
        <Icon name="help-buoy-outline" size={40} color="#fff" />
        <Text style={styles.bannerTitle}>{t('screen.helpTitle') || 'Help & Support'}</Text>
        <Text style={styles.bannerSub}>
          {t('screen.helpSub') || 'We are here to help you. Reach us anytime.'}
        </Text>
      </View>

      {/* Contact cards */}
      <View style={styles.contactRow}>
        {!!config.supportWhatsApp && (
          <TouchableOpacity style={styles.contactCard} onPress={openWhatsApp} activeOpacity={0.85}>
            <View style={[styles.contactIcon, { backgroundColor: '#25D366' }]}>
              <Icon name="logo-whatsapp" size={26} color="#fff" />
            </View>
            <Text style={styles.contactLabel}>{t('btn.whatsapp', 'WhatsApp')}</Text>
            <Text style={styles.contactSub}>{config.supportWhatsApp}</Text>
          </TouchableOpacity>
        )}

        <TouchableOpacity style={styles.contactCard} onPress={openEmail} activeOpacity={0.85}>
          <View style={[styles.contactIcon, { backgroundColor: THEME.primary }]}>
            <Icon name="mail-outline" size={26} color="#fff" />
          </View>
          <Text style={styles.contactLabel}>{t('btn.email', 'Email')}</Text>
          <Text style={styles.contactSub} numberOfLines={1}>{config.supportEmail}</Text>
        </TouchableOpacity>
      </View>

      {/* Note */}
      <View style={styles.noteBox}>
        <Icon name="time-outline" size={16} color={THEME.textLight} />
        <Text style={styles.noteText}>{supportHours}</Text>
      </View>

      {/* FAQ */}
      <Text style={styles.faqTitle}>
        {t('section.faq') || 'Frequently Asked Questions'}
      </Text>
      <View style={styles.faqList}>
        {faqs.map((item, i) => (
          <FaqRow key={i} item={item} />
        ))}
      </View>
    </ScrollView>
  );
};

export default HelpScreen;

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: THEME.bg },
  banner: {
    backgroundColor: THEME.primary, paddingTop: 32, paddingBottom: 28,
    alignItems: 'center', gap: 8, paddingHorizontal: 24,
  },
  bannerTitle: { fontSize: 22, fontWeight: '900', color: '#fff', marginTop: 8 },
  bannerSub: { fontSize: 14, color: 'rgba(255,255,255,0.8)', textAlign: 'center', lineHeight: 20 },
  contactRow: {
    flexDirection: 'row', gap: 12, marginHorizontal: 16, marginTop: 20,
  },
  contactCard: {
    flex: 1, backgroundColor: '#fff', borderRadius: 16, padding: 16,
    alignItems: 'center', gap: 6,
    shadowColor: '#000', shadowOpacity: 0.06, shadowRadius: 8, elevation: 3,
  },
  contactIcon: {
    width: 52, height: 52, borderRadius: 16,
    alignItems: 'center', justifyContent: 'center', marginBottom: 4,
  },
  contactLabel: { fontSize: 15, fontWeight: '800', color: THEME.text },
  contactSub: { fontSize: 12, color: THEME.textLight, textAlign: 'center' },
  noteBox: {
    flexDirection: 'row', alignItems: 'center', gap: 8,
    marginHorizontal: 16, marginTop: 12,
    backgroundColor: '#fff', borderRadius: 10, padding: 12,
    borderLeftWidth: 3, borderLeftColor: THEME.primary,
  },
  noteText: { flex: 1, fontSize: 13, color: THEME.textLight, lineHeight: 18 },
  faqTitle: {
    fontSize: 18, fontWeight: '800', color: THEME.text,
    marginHorizontal: 16, marginTop: 28, marginBottom: 10,
  },
  faqList: {
    marginHorizontal: 16, backgroundColor: '#fff', borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 6, elevation: 2,
  },
  faqItem: {
    padding: 16, borderBottomWidth: 1, borderBottomColor: THEME.border,
  },
  faqHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: 10 },
  faqQ: { flex: 1, fontSize: 15, fontWeight: '700', color: THEME.text, lineHeight: 22 },
  faqA: {
    fontSize: 14, color: THEME.textLight, lineHeight: 21,
    marginTop: 10, paddingLeft: 2,
  },
});
